import { getYaml } from '../services/yaml-service.js';
import { renderPowerShellItems, type PowerShellData, type PowerShellItem } from '../render/powershell.js';
import { renderFilterChips } from '../ui/filter-chips.js';
import { hasPrerenderedMarkup, whenIdle } from '../ui/lazy-hydrate.js';
import { initLayout } from '../layout.js';

function highlightCode(): void {
  window.setHighlightTheme?.(window.getIsDark?.() ?? false);
  const hljs = window.hljs;
  if (!hljs?.highlightAll) return;

  document.querySelectorAll('#powershell-content pre code').forEach((block) => {
    delete (block as HTMLElement).dataset.highlighted;
  });
  hljs.highlightAll();
}

function initPowerShellPage(): void {
  const categoryFilters = document.getElementById('powershell-category-filters');
  const fileFilters = document.getElementById('powershell-file-filters');
  const content = document.getElementById('powershell-content');
  const errorHost = document.getElementById('powershell-error');
  if (!categoryFilters || !fileFilters || !content || !errorHost) return;

  let allItems: PowerShellItem[] = [];
  let allCategories: string[] = [];
  let allFileNames: string[] = [];
  let selected = 'All';
  const keepPrerender = hasPrerenderedMarkup(content);

  const filteredItems = () => {
    if (selected === 'All') return allItems;
    if (allCategories.includes(selected)) {
      return allItems.filter((item) => item.Categories.includes(selected));
    }
    return allItems.filter((item) => item.FileName === selected);
  };

  const render = (updateContent = true) => {
    if (updateContent) {
      content.innerHTML = renderPowerShellItems(filteredItems());
    }
    highlightCode();

    renderFilterChips({
      container: categoryFilters,
      options: allCategories,
      selected,
      onSelect: (chip) => {
        selected = chip;
        render(true);
      },
      ariaLabel: 'Categories',
    });

    renderFilterChips({
      container: fileFilters,
      options: allFileNames,
      selected,
      showAll: false,
      onSelect: (chip) => {
        selected = chip;
        render(true);
      },
      ariaLabel: 'Files',
    });
  };

  whenIdle(() => {
    void (async () => {
      try {
        const data = await getYaml<PowerShellData>('PowerShell.yaml');
        allItems = (data?.PowerShell ?? [])
          .filter((item) => item.Title)
          .sort((a, b) => (a.Title ?? '').localeCompare(b.Title ?? ''));
        allCategories = [
          ...new Set(
            allItems.flatMap((item) => item.Categories).filter((c): c is string => Boolean(c))
          ),
        ].sort();
        allFileNames = [
          ...new Set(allItems.map((item) => item.FileName).filter((f): f is string => Boolean(f))),
        ].sort();
        selected = 'All';
        errorHost.hidden = true;
        render(!keepPrerender);
      } catch (error) {
        errorHost.hidden = false;
        errorHost.textContent = `Failed to load PowerShell data: ${error instanceof Error ? error.message : String(error)}`;
        content.innerHTML = '';
      }
    })();
  });
}

initLayout();
initPowerShellPage();
